import * as R from 'ramda';
import React, { useMemo, useState } from 'react';
import { compose, lifecycle } from 'react-recompose';
// components
import { Table } from '../../../components/table';
import ToggleBtn from '../../../components/toggle-btn';
import { FormGroupTitleMultiple } from '../../../components/form-group-title-multiple';
// features
import PC from '../../permission/role-permission';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// hooks
import useEditViewHook from '../../../hooks/use-edit-view';
// ui
import { Box, Flex } from '../../../ui';
// feature rate
import { PulseButton } from '../ui';
import { carrierRateReport, carrierRateSettings } from '../carrier/settings';
import { withAsyncCarrierRates, withShowIntegrationErrorsOnAvailableRates } from '../hocs';
import {
  CARRIER_AVAILABLE_RATES_REPORT_GUID,
  availableRatesAndCarriersTableSettings,
} from '../settings';
//////////////////////////////////////////////////

const AvailableRatesTable = (props) => {
  const { loading, onSelectRate, carrierRates } = props;

  const report = useMemo(() => {
    const fields = R.reject(({ name }) => {
      if (R.equals(name, GC.FIELD_TRANSPORTING_CARRIER)) return R.not(G.showTransportingCarrier());

      if (R.equals(name, 'configAccountNumber')) {
        return G.hasNotAmousCurrentUserPermissions(PC.CARRIER_INTEGRATION_ACCOUNT_NUMBER_READ);
      }

      return false;
    }, carrierRateReport.fields);

    return {
      fields,
      [GC.FIELD_GUID]: CARRIER_AVAILABLE_RATES_REPORT_GUID,
      type: G.getWindowLocale('titles:available-rates', 'Available Rates'),
    };
  }, []);

  const defaultReportFields = useMemo(
    () => R.map(R.prop(GC.FIELD_NAME), report.fields),
    [report],
  );

  const { uiReportFields, handleOpenEditView } = useEditViewHook({
    report,
    defaultReportFields,
    columnSettings: carrierRateSettings,
  });

  const sortedReport = useMemo(() => (
    G.isNilOrEmpty(uiReportFields) ? report : G.getSortedReport(uiReportFields, report)
  ), [report, uiReportFields]);

  return (
    <Table
      loading={loading}
      allChecked={false}
      showEditView={true}
      hasSelectable={true}
      report={sortedReport}
      shouldUseUIFilters={true}
      withResizableColumns={true}
      useSearchableColumns={true}
      useNewTitleFilterInputs={true}
      totalCount={carrierRates.length}
      columnSettings={carrierRateSettings}
      pagination={{ limit: 10, offset: 0 }}
      handleClickEditOrder={handleOpenEditView}
      onOptionClick={(guid, rate) => onSelectRate(rate)}
      tableSettings={availableRatesAndCarriersTableSettings}
      itemList={R.map(R.assoc('selected', false), carrierRates)}
      filterProps={G.getFilterPropsFromColumnSettings(carrierRateSettings)}
    />
  );
};

const AvailableRates = (props) => {
  const [showCustomerSpecificContracts, setShowCustomerSpecificContracts] = useState(false);

  const carrierRates = useMemo(() => {
    const rates = R.propOr([], 'carrierRates', props);

    if (R.not(showCustomerSpecificContracts)) return rates;

    return R.filter(
      (rate) => G.isNotNilAndNotEmpty(R.prop(GC.SYSTEM_LIST_GRANTED_ENTERPRISES, rate)),
      rates,
    );
  }, [props.carrierRates, showCustomerSpecificContracts]);

  return (
    <Box>
      <FormGroupTitleMultiple
        mb='0'
        jc='space-between'
        showArrowToggle={false}
        title={G.getWindowLocale('titles:carrier-rate', 'Carrier Rate')}
      >
        <Flex>
          <ToggleBtn
            checked={showCustomerSpecificContracts}
            onToggleHandler={setShowCustomerSpecificContracts}
            title={G.getWindowLocale('titles:customer-specific-contracts', 'Customer Specific Contracts')}
          />
          {
            G.isNotNilAndNotEmpty(R.path(['rateInfo', 'ratingIntegrationErrors'], props)) &&
            <Box
              mx='10px'
              p='3px 15px'
              fontSize='11px'
              cursor='pointer'
              fontWeight='bold'
              borderRadius='2px'
              bg={G.getTheme('colors.white')}
              color={G.getTheme('colors.light.darkRed')}
              onClick={props.handleShowIntegrationErrors}
            >
              <PulseButton animationDuration={props.animationDuration}>
                {G.getWindowLocale('actions:show-integration-errors', 'Show Integration Errors')}
              </PulseButton>
            </Box>
          }
          <Box
            mx='10px'
            p='3px 10px'
            fontSize='11px'
            cursor='pointer'
            fontWeight='bold'
            borderRadius='2px'
            onClick={props.closeModal}
            bg={G.getTheme('colors.white')}
            color={G.getTheme('colors.light.darkRed')}
          >
            {G.getWindowLocale('actions:close', 'Close')}
          </Box>
        </Flex>
      </FormGroupTitleMultiple>
      <AvailableRatesTable {...props} carrierRates={carrierRates} />
    </Box>
  );
};

const enhance = compose(
  withAsyncCarrierRates,
  withShowIntegrationErrorsOnAvailableRates,
  lifecycle({
    componentDidMount() {
      const { getAutoRatesRequest } = this.props;
      getAutoRatesRequest(this.props);
    },
  }),
);

export default enhance(AvailableRates);
